import React from 'react';
import { EyeOff, Sparkles } from 'lucide-react';
import { RenderedHtml, htmlIsEmpty } from 'modules/editor';
import { QuestionAnswerReview, canReview } from 'components/questions/QuestionAnswerReview';
import { useTranslation } from 'react-i18next';
import { ReviewExplanation, ReviewFill, ReviewMatchRow, ReviewOption } from './ReviewParts';
import { AnswerReviewHidden } from './AnswerReviewHidden';
import { SpeakingClips, type SpeakingClip } from 'modules/standardizedExam/components/report/SpeakingClips';
import { AiAnalysisPanel } from 'modules/answerAnalysis';
import { LockedInsight } from 'modules/examInsights';

/** Shared by the candidate result page and the author session review. Styling lives in `AnswerReviewStyles`. */

export interface AnswerReviewItem {
    questionId?: string;
    questionType: string;
    answer: string | string[];
    /** Absent when the author withheld the answer key. */
    correctAnswer?: string | string[];
    isCorrect: boolean;
    options:
        | Array<{ id: string; value: string; isCorrect?: boolean; isSelected: boolean }>
        | null;
    matchPairs: Array<{ key: string; correctValue?: string; selectedValue: string }> | null;
    answerRaw?: unknown;
    correctAnswerRaw?: unknown;
    questionData?: Record<string, any> | null;
    explanation?: string;
    aiReasoning?: string;
    /** Set by the API when an AI analysis can be offered for this answer; `texts` are those saved so far, by language. */
    aiAnalysis?: { available: boolean; texts: Record<string, string>; locked?: boolean };
    /** A reading passage: the AI analysis offered for each sub-question, by sub-question id. */
    aiSubAnalysis?: Record<string, { available: boolean; texts: Record<string, string>; locked?: boolean }>;
    /** A locked IELTS/SAT review left this answer's explanation out. */
    insightLocked?: boolean;
    /** IELTS Speaking: the recorded prompts with playable links and transcripts. */
    speakingRecordings?: SpeakingClip[];
}

export const hasAnswer = (answer: unknown): boolean => {
    if (answer == null || answer === '') return false;
    if (Array.isArray(answer)) return answer.length > 0 && answer.some((a) => a !== '' && a != null);
    if (typeof answer === 'object') {
        const values = Object.values(answer as Record<string, unknown>);
        return values.length > 0 && values.some((v) => v !== '' && v != null);
    }
    const str = String(answer).trim();
    return str !== '' && str !== '—' && str !== '[object Object]';
};

export const answerText = (answer: unknown): string => {
    if (answer == null) return '—';
    if (Array.isArray(answer)) return answer.join(', ');
    if (typeof answer === 'object') {
        const values = Object.values(answer as Record<string, unknown>);
        // IELTS Speaking stores one recording object per prompt; show a count, not their fields.
        // A live follow-up that was asked but never answered has its question and no recording.
        const isRecording = (v: unknown) => v != null && typeof v === 'object' && ('audioKey' in (v as object) || 'askedAt' in (v as object));
        if (values.length && values.every(isRecording)) {
            return `🎙 × ${values.filter((v) => 'audioKey' in (v as object)).length}`;
        }
        return Object.entries(answer as Record<string, unknown>)
            .map(([k, v]) => `${k} → ${v}`)
            .join('; ');
    }
    return String(answer || '—');
};

interface AnswerReviewBodyProps {
    answer: AnswerReviewItem;
    /** The API withheld the answer key: show the candidate's own answers only, plus one quiet note. */
    correctAnswersHidden?: boolean;
    /** Only the candidate's own result page passes this: it is what lets the AI analysis be asked for. */
    attemptId?: string;
}

export const AnswerReviewBody: React.FC<AnswerReviewBodyProps> = ({ answer: current, correctAnswersHidden = false, attemptId }) => {
    const { t } = useTranslation();
    // Speaking is graded on bands, never against a key: show what was said, prompt by prompt.
    if (current.questionType === 'SPEAKING') {
        return (
            <div className="ts-q-options min-w-0">
                <SpeakingClips
                    recordings={current.speakingRecordings ?? []}
                    variant="review"
                    modelAnswerFor={attemptId && current.questionId ? { attemptId, questionId: current.questionId } : undefined}
                />
                {current.aiReasoning && <AiFeedback text={current.aiReasoning} />}
            </div>
        );
    }
    if (correctAnswersHidden) {
        return (
            <div className="ts-q-options">
                <AnswerReviewHidden answer={current} />
                {current.aiReasoning && <AiFeedback text={current.aiReasoning} />}
                <p className="mt-3 flex items-center gap-2 text-[13px] text-muted-foreground">
                    <EyeOff className="h-4 w-4 shrink-0 text-muted-foreground/70" aria-hidden />
                    {t('result_correct_answers_hidden')}
                </p>
            </div>
        );
    }
    return (
        <div className="ts-q-options">
            {current.options &&
                current.options.map((opt) => (
                    <ReviewOption
                        key={opt.id}
                        html={opt.value || ''}
                        isCorrect={opt.isCorrect ?? false}
                        isSelected={opt.isSelected}
                        multi={current.questionType === 'MULTIPLE_RESPONSE'}
                    />
                ))}

            {!current.options &&
                !current.matchPairs &&
                (canReview(current.questionType) ? (
                    <QuestionAnswerReview
                        type={current.questionType}
                        answerRaw={current.answerRaw}
                        correctAnswerRaw={current.correctAnswerRaw}
                        questionData={current.questionData}
                        renderSubQuestionFooter={
                            attemptId && current.questionId && current.aiSubAnalysis
                                ? (subId) => {
                                      const offer = current.aiSubAnalysis?.[subId];
                                      if (!offer?.available) return null;
                                      if (offer.locked) {
                                          return <LockedInsight title={t('exam_insights.why_title')} description={t('exam_insights.why_body')} />;
                                      }
                                      return (
                                          <AiAnalysisPanel
                                              attemptId={attemptId}
                                              questionId={current.questionId as string}
                                              subQuestionId={subId}
                                              saved={offer.texts}
                                              className="mt-3"
                                          />
                                      );
                                  }
                                : undefined
                        }
                    />
                ) : (
                    <ReviewFill
                        state={!hasAnswer(current.answer) ? 'empty' : current.isCorrect ? 'correct' : 'incorrect'}
                        given={<RenderedHtml html={answerText(current.answer)} inline />}
                        correct={
                            hasAnswer(current.correctAnswer) ? (
                                <RenderedHtml html={answerText(current.correctAnswer)} inline />
                            ) : null
                        }
                    />
                ))}

            {current.matchPairs && (
                <div className="space-y-2">
                    {current.matchPairs.map((pair, idx) => {
                        const selVal = (pair.selectedValue || '').trim();
                        const corrVal = (pair.correctValue || '').trim();
                        return (
                            <ReviewMatchRow
                                key={pair.key || idx}
                                state={selVal === '' ? 'empty' : selVal.toLowerCase() === corrVal.toLowerCase() ? 'correct' : 'incorrect'}
                                label={<RenderedHtml html={pair.key || ''} inline />}
                                given={<RenderedHtml html={pair.selectedValue} inline />}
                                correct={<RenderedHtml html={pair.correctValue || ''} inline />}
                            />
                        );
                    })}
                </div>
            )}

            {(() => {
                const explanation = current.explanation || current.questionData?.explanation;
                if (!explanation || htmlIsEmpty(explanation)) {
                    // A locked IELTS/SAT review where the API held something back: the "why" comes with the plan
                    // (one AI analysis per test stays free). Nothing hidden, nothing to lock.
                    if (current.aiAnalysis?.locked || current.insightLocked) {
                        return (
                            <LockedInsight
                                title={t('exam_insights.why_title')}
                                description={t('exam_insights.why_body')}
                            />
                        );
                    }
                    // No explanation from the author: offer an AI one, but only where the API said it may be asked for.
                    return attemptId && current.questionId && current.aiAnalysis?.available ? (
                        <AiAnalysisPanel attemptId={attemptId} questionId={current.questionId} saved={current.aiAnalysis.texts} />
                    ) : null;
                }
                if (current.questionType === 'PASSAGE' && current.questionData?.subQuestions?.length) {
                    return null;
                }
                return <ReviewExplanation html={explanation} />;
            })()}

            {current.aiReasoning && <AiFeedback text={current.aiReasoning} />}
        </div>
    );
};

const AiFeedback: React.FC<{ text: string }> = ({ text }) => {
    const { t } = useTranslation();
    return (
        <div className="mt-4 rounded-xl border border-border bg-background p-4">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
                <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
                <span>{t('ai_feedback', 'AI Feedback')}</span>
            </div>
            <div className="mt-2.5 border-t border-border pt-2.5 text-sm leading-relaxed text-foreground/90">
                <p className="whitespace-pre-wrap">{text}</p>
            </div>
        </div>
    );
};

/** Render once on a host page that does NOT already define the `.ts-shell` theme. `TestResults` must not. */
export const AnswerReviewStyles: React.FC = () => (
    <style>{`
.ts-shell {
    --ts-primary: #171717;
    --ts-primary-fg: #FFFFFF;
    --ts-primary-50: #F4F4F5;
    --ts-primary-200: #D4D4D8;
    --ts-primary-600: #000000;
    --ts-ring: rgba(23, 23, 23, 0.16);
    --ts-bg: #FFFFFF;
    --ts-card-bg: #FFFFFF;
    --ts-gray-100: #F3F4F6;
    --ts-gray-200: #E5E7EB;
    --ts-gray-300: #D1D5DB;
    --ts-gray-400: #9CA3AF;
    --ts-gray-500: #6B7280;
    --ts-gray-600: #4B5563;
    --ts-gray-700: #374151;
    --ts-gray-900: #111827;
    --ts-success-bg: #F0FDF4;
    --ts-success-border: #BBF7D0;
    --ts-success-text: #15803D;
    --ts-success-strong: #16A34A;
    --ts-success-grad-end: #22C55E;
    --ts-danger-bg: #FEF2F2;
    --ts-danger-border: #FECACA;
    --ts-danger-text: #B91C1C;
    --ts-danger-strong: #DC2626;
    --ts-danger-grad-end: #EF4444;
}
.dark .ts-shell {
    --ts-primary: #FAFAFA;
    --ts-primary-fg: #0A0A0A;
    --ts-primary-50: rgba(250, 250, 250, 0.10);
    --ts-primary-200: rgba(250, 250, 250, 0.32);
    --ts-primary-600: #E4E4E7;
    --ts-ring: rgba(250, 250, 250, 0.22);
    --ts-bg: hsl(var(--background));
    --ts-card-bg: hsl(var(--card));
    --ts-gray-100: hsl(var(--muted));
    --ts-gray-200: hsl(var(--border));
    --ts-gray-300: hsl(220 13% 35%);
    --ts-gray-400: hsl(220 9% 55%);
    --ts-gray-500: hsl(var(--muted-foreground));
    --ts-gray-600: hsl(220 9% 70%);
    --ts-gray-700: hsl(220 9% 80%);
    --ts-gray-900: hsl(var(--foreground));
    --ts-success-bg: rgba(34, 197, 94, 0.14);
    --ts-success-border: rgba(34, 197, 94, 0.4);
    --ts-success-text: #4ADE80;
    --ts-success-strong: #22C55E;
    --ts-success-grad-end: #4ADE80;
    --ts-danger-bg: rgba(239, 68, 68, 0.14);
    --ts-danger-border: rgba(239, 68, 68, 0.4);
    --ts-danger-text: #FCA5A5;
    --ts-danger-strong: #EF4444;
    --ts-danger-grad-end: #FCA5A5;
}
.ts-q-options { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.ts-review-opt {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 16px; border-radius: 10px;
    border: 1px solid var(--ts-gray-200); background: var(--ts-card-bg);
}
.ts-review-opt-correct { border-color: var(--ts-success-border); background: var(--ts-success-bg); }
.ts-review-opt-incorrect { border-color: var(--ts-danger-border); background: var(--ts-danger-bg); }
.ts-review-opt-mark {
    width: 20px; height: 20px; border-radius: 999px;
    border: 2px solid var(--ts-gray-300);
    flex-shrink: 0; margin-top: 1px;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--ts-card-bg);
}
.ts-review-opt-mark-square { border-radius: 6px; }
.ts-review-opt-mark-correct { border-color: var(--ts-success-strong); background: var(--ts-success-strong); }
.ts-review-opt-mark-incorrect { border-color: var(--ts-danger-strong); background: var(--ts-danger-strong); }
.ts-review-opt-mark-fill { width: 7px; height: 7px; background: #fff; border-radius: 999px; }
.ts-opt-letter {
    font-size: 11px; font-weight: 700;
    color: var(--ts-gray-500); margin-right: 4px; margin-top: 2px;
    min-width: 12px;
}
.ts-review-opt-correct .ts-opt-letter { color: var(--ts-success-text); }
.ts-review-opt-incorrect .ts-opt-letter { color: var(--ts-danger-text); }
.ts-opt-text { font-size: 14px; line-height: 1.55; color: var(--ts-gray-700); }
.ts-review-opt-correct .ts-opt-text { color: var(--ts-success-text); font-weight: 500; }
.ts-review-opt-incorrect .ts-opt-text { color: var(--ts-danger-text); font-weight: 500; }
.ts-review-opt-icon { margin-left: auto; flex-shrink: 0; }
.ts-review-fill {
    padding: 14px 16px; border-radius: 10px; border: 1px solid var(--ts-gray-200);
    background: var(--ts-card-bg);
}
.ts-review-fill-empty { background: var(--ts-gray-100); }
.ts-review-fill-correct { background: var(--ts-success-bg); border-color: var(--ts-success-border); }
.ts-review-fill-incorrect { background: var(--ts-danger-bg); border-color: var(--ts-danger-border); }
.ts-review-fill-label {
    font-size: 11px; font-weight: 600;
    color: var(--ts-gray-500);
    text-transform: uppercase; letter-spacing: 0.04em;
    margin-bottom: 4px;
}
.ts-review-fill-value { font-size: 14px; font-weight: 500; color: var(--ts-gray-900); overflow-wrap: anywhere; }
.ts-review-fill-empty .ts-review-fill-value { color: var(--ts-gray-500); font-style: italic; }
.ts-review-fill-correct .ts-review-fill-value { color: var(--ts-success-text); }
.ts-review-fill-incorrect .ts-review-fill-value { color: var(--ts-danger-text); }
.ts-review-match-row {
    display: grid; grid-template-columns: 1fr auto 1fr;
    gap: 8px; align-items: center;
}
.ts-review-match-key {
    padding: 10px 12px; border-radius: 8px;
    background: var(--ts-gray-100); color: var(--ts-gray-900);
    font-size: 14px; font-weight: 500;
}
.ts-review-match-eq { color: var(--ts-gray-400); font-size: 12px; }
.ts-review-match-val {
    padding: 10px 12px; border-radius: 8px;
    border: 1px solid var(--ts-gray-200); background: var(--ts-card-bg);
    font-size: 14px; font-weight: 500;
}
.ts-review-match-val-empty { background: var(--ts-gray-100); color: var(--ts-gray-500); font-style: italic; }
.ts-review-match-val-correct {
    background: var(--ts-success-bg); border-color: var(--ts-success-border); color: var(--ts-success-text);
}
.ts-review-match-val-incorrect {
    background: var(--ts-danger-bg); border-color: var(--ts-danger-border); color: var(--ts-danger-text);
}
.ts-review-match-correction {
    display: block; font-size: 12px; font-weight: 400;
    color: var(--ts-success-text); margin-top: 2px;
}
`}</style>
);
